import React, { useEffect, useState } from "react";
import { Box, Stack, Switch } from "@mui/joy";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
  appBiometricEnabledAtom,
  appLockEnabledAtom,
  appLockPinHashAtom,
  PIN_SET_PLACEHOLDER,
} from "../../recoil/appLockAtoms";
import { theme } from "../../styles/theme";
import {
  useDisableAppLock,
  useUpdateBiometricEnabled,
} from "../../api/appLockQueries";
import { appLockApi } from "../../api/appLockApi";
import { nativeBridge } from "../../utils/nativeBridge";
import CustomTypography from "../../components/common/CustomTypography";
import TaskHeader from "../../components/task/header/TaskHeader";
import { userAtom } from "../../recoil/userAtom";
import ContentTemplateV2 from "../../components/task/content/ContentTemplateV2";
import { useNavigate } from "react-router-dom";
import useIsInappWebview from "../../hooks/useIsInappWebview";

const SecuritySettingsTask: React.FC = () => {
  const user = useRecoilValue(userAtom);
  const [enabled, setEnabled] = useRecoilState(appLockEnabledAtom);
  const setPinHash = useSetRecoilState(appLockPinHashAtom);
  const navigate = useNavigate();

  const disableMutation = useDisableAppLock();
  const updateBioMutation = useUpdateBiometricEnabled();
  const isInappWebview = useIsInappWebview();

  type StyledSwitchProps = React.ComponentProps<typeof Switch> & {
    trackHeight?: number | string;
    trackWidth?: number | string;
    thumbSize?: number | string;
    trackColorOn?: string;
    trackColorOff?: string;
    thumbColorOn?: string;
    thumbColorOff?: string;
  };

  const StyledSwitch: React.FC<StyledSwitchProps> = ({
    trackHeight = 24,
    trackWidth = 40,
    thumbSize = 20,
    trackColorOn = theme.vars.palette.Surface.Highlight,
    trackColorOff = theme.vars.palette.Surface.Backward,
    thumbColorOn = theme.vars.palette.Content.Negative,
    thumbColorOff = theme.vars.palette.Content.Negative,
    sx,
    ...rest
  }) => {
    const h =
      typeof trackHeight === "number" ? `${trackHeight}px` : trackHeight;
    const w = trackWidth
      ? typeof trackWidth === "number"
        ? `${trackWidth}px`
        : trackWidth
      : `calc(${h} * 1.8)`;
    const t = typeof thumbSize === "number" ? `${thumbSize}px` : thumbSize;
    return (
      <Switch
        sx={{
          "--Switch-trackHeight": h,
          "--Switch-trackWidth": w,
          "--Switch-thumbSize": t,
          "& .MuiSwitch-track": {
            backgroundColor: trackColorOff,
            border: "none",
            boxShadow: "none",
            transition:
              "background-color 200ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms cubic-bezier(0.22, 1, 0.36, 1)",
          },
          "&.Mui-checked .MuiSwitch-track": {
            backgroundColor: trackColorOn,
            border: "none",
            boxShadow: "none",
          },
          "& .MuiSwitch-thumb": {
            backgroundColor: thumbColorOff,
            border: "none",
            boxShadow: "none",
            transition:
              "transform 220ms cubic-bezier(0.22, 1, 0.36, 1), background-color 200ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 200ms cubic-bezier(0.22, 1, 0.36, 1)",
            willChange: "transform",
          },
          "&.Mui-checked .MuiSwitch-thumb": {
            backgroundColor: thumbColorOn,
            border: "none",
            boxShadow: "none",
          },
          "&.Mui-focusVisible .MuiSwitch-thumb, & .MuiSwitch-thumb:focus": {
            outline: "none",
          },
          ...sx,
        }}
        {...rest}
      />
    );
  };

  // Biometric support state
  const [bioEnabled, setBioEnabled] = useRecoilState(appBiometricEnabledAtom);
  const [bioSupported, setBioSupported] = useState<boolean>(false);
  const [bioChecking, setBioChecking] = useState<boolean>(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const info = await nativeBridge.getBiometricStatus(
          isInappWebview ? 3500 : 100
        );
        if (!mounted) return;
        setBioSupported(!!info?.available);
      } catch {
        if (!mounted) return;
        setBioSupported(false);
      } finally {
        if (mounted) setBioChecking(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const refresh = async () => {
      try {
        const s = await appLockApi.get();
        setEnabled(!!s.isEnabled);
        // Sync strictly with server to avoid stale local placeholder
        setPinHash(s.hasPin ? PIN_SET_PLACEHOLDER : null);
        setBioEnabled(!!s.isBiometricEnabled);
      } catch {
        // ignore
      }
    };
    const onVisibility = () => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "visible"
      ) {
        refresh();
      }
    };
    const onFocus = () => {
      refresh();
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("focus", onFocus);
    }
    // Also refresh once when component mounts to ensure state sync
    refresh();
    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("focus", onFocus);
      }
    };
  }, [setEnabled, setPinHash, setBioEnabled]);

  const handleAppLockToggle = async (next: boolean) => {
    try {
      if (next) {
        // OFF -> ON: Always require new PIN setup
        navigate(`/pin-setup`);
        return;
      } else {
        // ON -> OFF: Require authentication first; actual disable happens after successful auth in Unlock screen
        navigate(`/unlock?next=disable`);
        return;
      }
    } catch (err) {
      // 실패 시 UI 되돌림
      setEnabled(!next);
      console.error("처리에 실패했습니다. 잠시 후 다시 시도하세요.");
    }
  };

  const handleBioToggle = async (next: boolean) => {
    try {
      if (next) {
        const res = await nativeBridge.requestBiometric({
          promptMessage: "생체 인증을 활성화합니다",
          cancelLabel: "취소",
          disableDeviceFallback: false,
        });
        if (res?.success) {
          const prev = bioEnabled;
          setBioEnabled(true);
          try {
            const data = await updateBioMutation.mutateAsync(true);
            if (typeof data?.isBiometricEnabled === "boolean") {
              setBioEnabled(!!data.isBiometricEnabled);
            }
          } catch {
            setBioEnabled(prev);
            console.error(
              "설정 저장에 실패했습니다. 잠시 후 다시 시도하세요."
            );
          }
        } else {
          setBioEnabled(false);
          console.error(
            res?.error === "not_enrolled"
              ? "생체 정보가 등록되어 있지 않습니다. 설정에서 등록 후 다시 시도하세요."
              : "생체 인증에 실패했습니다."
          );
        }
      } else {
        // 생체 인증 끄기: 인증 화면으로 이동 후 성공 시 해제 처리
        navigate(`/unlock?next=disable_bio`);
        return;
      }
    } catch (err: any) {
      setBioEnabled(false);
      console.error(
        err?.message || "생체 인증 처리 중 오류가 발생했습니다."
      );
    }
  };

  if (bioChecking) {
    return <></>;
  }

  return (
    <>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <TaskHeader
          key={`app-lock-${user?.userId}`}
          taskKey={`app-lock-${user?.userId}`}
          title={"앱 잠금"}
        />
      </Box>
      <ContentTemplateV2>
        <Stack spacing={"12px"}>
          <Stack
            sx={{
              p: 1,
              borderRadius: "16px",
              backgroundColor: theme.vars.palette.Surface.Forward,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              p={"12px"}
              onClick={() => handleAppLockToggle(!enabled)}
              sx={{ cursor: "pointer" }}
            >
              <CustomTypography
                variant={"Body"}
                sx={{
                  fontWeight: 700,
                  color: theme.vars.palette.Content.Strong,
                }}
              >
                앱 열 때 비밀번호 묻기
              </CustomTypography>
              <StyledSwitch
                checked={enabled}
                onChange={async (e) => {
                  const next = e.target.checked;
                  handleAppLockToggle(next);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
            </Stack>
            {enabled && bioSupported && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                p={"12px"}
                onClick={() => handleBioToggle(!bioEnabled)}
                sx={{ cursor: "pointer" }}
              >
                <CustomTypography
                  variant={"Body"}
                  sx={{
                    fontWeight: 700,
                    color: theme.vars.palette.Content.Strong,
                  }}
                >
                  생체인증 사용
                </CustomTypography>
                <StyledSwitch
                  checked={bioEnabled}
                  onChange={async (e) => {
                    const next = e.target.checked;
                    handleBioToggle(next);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                />
              </Stack>
            )}
          </Stack>
          <Stack px={1}>
            <CustomTypography
              variant={"Caption"}
              sx={{ fontWeight: 500, color: theme.vars.palette.Content.Weak }}
            >
              {!enabled
                ? "앱 잠금을 켜면 잠금 화면에도 알림 내용이 표시되지 않아요."
                : "비밀번호를 변경하려면 잠금 기능을 해제한 뒤 다시 설정해주세요."}
            </CustomTypography>
          </Stack>
        </Stack>
      </ContentTemplateV2>
    </>
  );
};

export default SecuritySettingsTask;
